import { boxPlanningKeys } from "@/modules/boxPlanning/gateway/boxPlanningKeys"
import { BoxProgramDetailsEntity, BoxProgramItemEntity } from "@/modules/boxProgrammation/entities/boxProgramEntity"
import type { BoxProgramFormFields } from "@/modules/boxProgrammation/formik/boxProgramFormTypes"
import { boxProgrammationKeys } from "@/modules/boxProgrammation/gateway/boxProgramKeys"
import type { Paginated } from "@/modules/utils/paginated"
import { DEFAULT_PAGE_SIZE_BOX_PROGRAM } from "@/modules/utils/paginated"
import { useServicesContext } from "@modules/core/services/services.context"
import { useMutation, useQuery, useQueryClient, useSuspenseQuery } from "@tanstack/react-query"
import { useState } from "react"

export const useBoxProgram = (id: string) => {
  const { boxProgramApi } = useServicesContext()
  return useSuspenseQuery({
    queryKey: boxProgrammationKeys.programmation(id),
    queryFn: async () => {
      const result = await boxProgramApi.getBoxProgram(id)
      return BoxProgramDetailsEntity.fromDto(result)
    },
  })
}

export const useFetchBoxProgram = (id: string | undefined) => {
  const { boxProgramApi } = useServicesContext()
  return useQuery({
    queryKey: boxProgrammationKeys.programmation(id),
    queryFn: async () => {
      if (!id) throw new Error("Box programmation ID is required")
      const result = await boxProgramApi.getBoxProgram(id)
      return BoxProgramDetailsEntity.fromDto(result)
    },
    enabled: !!id,
  })
}

export const useCreateBoxProgram = () => {
  const { boxProgramApi } = useServicesContext()
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: async ({ boxProgram }: { boxProgram: BoxProgramFormFields }) => {
      const result = await boxProgramApi.createBoxProgram(
        BoxProgramDetailsEntity.fromFormikToCreateInputDto(boxProgram),
      )
      return BoxProgramDetailsEntity.fromDto(result)
    },
    onSuccess: (data: BoxProgramDetailsEntity) => {
      void queryClient.refetchQueries({ queryKey: boxProgrammationKeys.programmations() })
      void queryClient.setQueryData(boxProgrammationKeys.programmation(data.id), data)
    },
  })
}

export const useUpdateBoxProgram = () => {
  const { boxProgramApi } = useServicesContext()
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: async ({ boxProgramId, boxProgram }: { boxProgramId: string; boxProgram: BoxProgramFormFields }) => {
      const result = await boxProgramApi.updateBoxProgram(
        boxProgramId,
        BoxProgramDetailsEntity.fromFormikToUpdateInputDto(boxProgram),
      )
      return BoxProgramDetailsEntity.fromDto(result)
    },
    onSuccess: (data: BoxProgramDetailsEntity) => {
      void queryClient.refetchQueries({ queryKey: boxProgrammationKeys.programmations() })
      void queryClient.invalidateQueries({ queryKey: boxPlanningKeys.slots() })
      void queryClient.setQueryData(boxProgrammationKeys.programmation(data.id), data)
    },
  })
}

export const useBoxPrograms = (options?: { defaultPage?: number; defaultPageSize?: number }) => {
  const { boxProgramApi } = useServicesContext()
  const [page, setPage] = useState(options?.defaultPage ?? 0)
  const [pageSize, setPageSize] = useState(options?.defaultPageSize ?? DEFAULT_PAGE_SIZE_BOX_PROGRAM)

  const { data, isLoading, error } = useSuspenseQuery<Paginated<BoxProgramItemEntity>>({
    queryKey: boxProgrammationKeys.programmations({ page, pageSize }),
    queryFn: async () => {
      return await boxProgramApi.getBoxPrograms(page, pageSize)
    },
  })

  const handleSetPage = (newPageNumber: number) => {
    setPage(newPageNumber)
  }

  const handlePreviousPage = () => {
    if (data.page > 0) {
      setPage((prev) => prev - 1)
    }
  }

  const handleNextPage = () => {
    if (data.page < data.totalPages - 1) {
      setPage((prev) => prev + 1)
    }
  }

  return {
    data: data.content.map((item) => BoxProgramItemEntity.fromDto(item)),
    isLoading,
    error,
    pageSize,
    setPageSize,
    handleSetPage,
    handlePreviousPage,
    handleNextPage,
    hasPreviousPage: data.page > 0,
    hasNextPage: data.page < data.totalPages - 1,
    totalElements: data.totalElements,
  }
}

export const useDeleteBoxProgram = () => {
  const { boxProgramApi } = useServicesContext()
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: async (boxProgramId: string) => {
      await boxProgramApi.deleteBoxProgram(boxProgramId)
      return boxProgramId
    },
    onSuccess: (boxProgramId: string) => {
      void queryClient.removeQueries({ queryKey: boxProgrammationKeys.programmation(boxProgramId) })
      void queryClient.invalidateQueries({ queryKey: boxPlanningKeys.slots() })
      void queryClient.refetchQueries({ queryKey: boxProgrammationKeys.programmations() })
    },
  })
}
